import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Plus, 
  Trash2, 
  Loader2,
  FolderOpen,
  Save
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCategoriesPage = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, [token]);

  const fetchCategories = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/admin/product-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/product-categories`,
        { name: newCategory.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCategories(response.data.categories || []);
      setNewCategory('');
      setMessage({ type: 'success', text: 'Kategori tilføjet!' });
      
    } catch (err) {
      console.error('Failed to add category:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Kunne ikke tilføje kategori' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne kategori?')) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/admin/product-categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCategories(response.data.categories || []);
      setMessage({ type: 'success', text: 'Kategori slettet!' });
      
    } catch (err) {
      console.error('Failed to delete category:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Kunne ikke slette kategori' 
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Produktkategorier</h1>
        <p className="text-slate-600 mt-1">
          Administrer kategorier til billedupload
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Add Category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Tilføj ny kategori
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Kategorinavn..."
              className="flex-1"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              data-testid="new-category-input"
            />
            <Button
              onClick={handleAddCategory}
              disabled={!newCategory.trim() || saving}
              data-testid="add-category-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="ml-2">Tilføj</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Categories List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Eksisterende kategorier ({categories.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              Ingen kategorier oprettet endnu
            </p>
          ) : (
            <div className="space-y-2">
              {categories.map((cat) => (
                <div 
                  key={cat.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FolderOpen className="h-4 w-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{cat.name}</span>
                  </div>
                  <Button
                    onClick={() => handleDeleteCategory(cat.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-cat-${cat.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductCategoriesPage;
